// SPDX-License-Identifier: GPL-3.0-or-later
//
// Orchestrate a full generation: profile + job → AI → JSON → template → tex →
// SwiftLaTeX → PDF. All three documents (CV, cover letter, interview prep)
// share this pipeline; only the prompts + schemas + templates + filenames
// differ.

import { complete } from './ai/client';
import { checkCvHallucinations, type HallucinationReport } from './ai/hallucination';
import { parseJson } from './ai/parse';
import { cvSystemPrompt, cvUserPrompt } from './ai/prompts/cv';
import { coverSystemPrompt, coverUserPrompt } from './ai/prompts/cover';
import { interviewSystemPrompt, interviewUserPrompt } from './ai/prompts/interview';
import {
  isCoverDoc,
  isCvDoc,
  isInterviewDoc,
  type CoverDoc,
  type CvDoc,
  type InterviewDoc,
} from './ai/schemas';
import { readJobTxt } from './fs/jobtxt';
import { readProfile } from './fs/profile';
import { loadCustomTemplate } from './fs/templates';
import { sanitizeFilename } from './fs/util';
import { compileTypstToPdf } from './typst/engine';
import { renderTemplate } from './typst/render';
import { coverTemplate } from './typst/templates/cover';
import { cvTemplate } from './typst/templates/cv';
import { interviewTemplate } from './typst/templates/interview';
import { getManifest, getRoot, setGeneratedRef } from './store/applications';
import type { Application, GenRef, Manifest } from './types';

export type DocKind = 'cv' | 'cover' | 'interview';

export type GenerateStage =
  | 'state'
  | 'profile'
  | 'job'
  | 'ai'
  | 'parse'
  | 'render'
  | 'write'
  | 'compile';

export interface GenerateSuccess {
  ok: true;
  kind: DocKind;
  texRelPath: string;
  texFilename: string;
  pdfRelPath?: string;
  pdfFilename?: string;
  log?: string;
  hallucinations?: HallucinationReport;
}

export interface GenerateFailure {
  ok: false;
  kind: DocKind;
  stage: GenerateStage;
  message: string;
  raw?: string;
  log?: string;
}

export type GenerateOutcome = GenerateSuccess | GenerateFailure;

export interface InterviewInputs {
  interviewerName: string;
  interviewerRole?: string;
  interviewType?: string;
}

// ----- Shared helpers -----

async function resolveFolder(
  root: FileSystemDirectoryHandle,
  relPath: string,
): Promise<FileSystemDirectoryHandle | null> {
  if (!relPath) return null;
  const parts = relPath.split('/').filter(Boolean);
  let cur: FileSystemDirectoryHandle = root;
  for (const part of parts) {
    try {
      cur = await cur.getDirectoryHandle(part);
    } catch {
      return null;
    }
  }
  return cur;
}

async function writeTextFile(
  dir: FileSystemDirectoryHandle,
  name: string,
  contents: string,
): Promise<void> {
  const handle = await dir.getFileHandle(name, { create: true });
  const writable = await handle.createWritable();
  try { await writable.write(contents); }
  finally { await writable.close(); }
}

async function writeBinaryFile(
  dir: FileSystemDirectoryHandle,
  name: string,
  bytes: Uint8Array,
): Promise<void> {
  const handle = await dir.getFileHandle(name, { create: true });
  const writable = await handle.createWritable();
  try {
    // Wrap in a Blob to sidestep TS's strict ArrayBuffer-vs-SharedArrayBuffer
    // narrowing on Uint8Array.write().
    await writable.write(new Blob([new Uint8Array(bytes)]));
  } finally {
    await writable.close();
  }
}

async function getCommonInputs(app: Application): Promise<
  | {
      ok: true;
      root: FileSystemDirectoryHandle;
      manifest: Manifest;
      profileText: string;
      previousCovers: string[];
      description: string;
    }
  | GenerateFailure
> {
  const root = getRoot();
  const manifest = getManifest();
  if (!root || !manifest) {
    return { ok: false, kind: 'cv', stage: 'state', message: 'Folder not selected or manifest not loaded.' };
  }
  const profile = await readProfile(root);
  if (!profile?.rawText || !profile.rawText.trim()) {
    return {
      ok: false, kind: 'cv', stage: 'profile',
      message: 'No profile saved yet. Open Settings → Profile and paste your CV text first.',
    };
  }
  const jobTxt = await readJobTxt(root, app);
  const description = jobTxt?.description ?? '';
  const previousCovers = (profile.previousCovers ?? [])
    .map((s) => (typeof s === 'string' ? s.trim() : ''))
    .filter((s) => s.length > 0);
  return { ok: true, root, manifest, profileText: profile.rawText, previousCovers, description };
}

// Routed through the store so the in-memory cache + multi-tab broadcast both
// see the update. Earlier we wrote directly to disk and left the cache stale,
// which made the meta line on JobDetail show "· .typ only" until reload.
async function persistGenerated(
  _manifest: Manifest,
  app: Application,
  kind: DocKind,
  ref: GenRef,
): Promise<void> {
  await setGeneratedRef(app.id, kind, ref);
}

async function compileAndWritePdf(
  folder: FileSystemDirectoryHandle,
  source: string,
  pdfFilename: string,
): Promise<{ pdfFilename?: string; log: string; ok: boolean }> {
  const compile = await compileTypstToPdf(source);
  if (compile.pdf && compile.status === 0) {
    await writeBinaryFile(folder, pdfFilename, compile.pdf);
    return { pdfFilename, log: compile.log, ok: true };
  }
  return { log: compile.log, ok: false };
}

// ----- Generic generation core -----

interface GenSpec<T> {
  kind: DocKind;
  filenameBase: string;       // e.g. "CV", "Cover Letter", "Interview Prep"
  systemPrompt: string;
  userPrompt: string;
  guard: (x: unknown) => x is T;
  template: string;
  maxTokens: number;
  // Optional post-parse hook (e.g. hallucination check for CV).
  postParse?: (doc: T) => Partial<GenerateSuccess>;
}

async function runGeneration<T>(
  app: Application,
  spec: GenSpec<T>,
  pre: { root: FileSystemDirectoryHandle; manifest: Manifest },
): Promise<GenerateOutcome> {
  // 1) AI
  let aiContent: string;
  try {
    const res = await complete({
      system: spec.systemPrompt,
      user: spec.userPrompt,
      maxTokens: spec.maxTokens,
    });
    aiContent = res.content;
  } catch (err) {
    return { ok: false, kind: spec.kind, stage: 'ai', message: err instanceof Error ? err.message : String(err) };
  }

  // 2) Parse
  const parsed = parseJson(aiContent, spec.guard);
  if (!parsed.ok) {
    return { ok: false, kind: spec.kind, stage: 'parse', message: parsed.message, raw: parsed.raw };
  }

  // 3) Render — prefer a user-overridden template if present.
  const customTpl = await loadCustomTemplate(pre.root, spec.kind);
  const template = customTpl ?? spec.template;
  let source: string;
  try { source = renderTemplate(template, parsed.value); }
  catch (err) {
    return { ok: false, kind: spec.kind, stage: 'render', message: err instanceof Error ? err.message : String(err) };
  }

  // 4) Write the Typst source (.typ).
  const folder = await resolveFolder(pre.root, app.folderRelPath);
  if (!folder) return { ok: false, kind: spec.kind, stage: 'write', message: 'Job folder not found on disk.' };

  const baseName = sanitizeFilename(`${spec.filenameBase} - ${app.jobTitle}`).slice(0, 120)
    || spec.filenameBase;
  const sourceFilename = `${baseName}.typ`;
  const pdfFilename = `${baseName}.pdf`;

  try { await writeTextFile(folder, sourceFilename, source); }
  catch (err) {
    return { ok: false, kind: spec.kind, stage: 'write', message: err instanceof Error ? err.message : String(err) };
  }

  const texRelPath = `${app.folderRelPath}/${sourceFilename}`;

  // 5) Compile to PDF (best effort — return .typ success even if compile fails).
  let pdfRelPath: string | undefined;
  let log: string | undefined;
  try {
    const out = await compileAndWritePdf(folder, source, pdfFilename);
    log = out.log;
    if (out.ok) pdfRelPath = `${app.folderRelPath}/${pdfFilename}`;
  } catch (err) {
    log = err instanceof Error ? err.message : String(err);
  }

  // 6) Persist manifest
  const ref: GenRef = { texRelPath, generatedAt: new Date().toISOString() };
  if (pdfRelPath) ref.pdfRelPath = pdfRelPath;
  try { await persistGenerated(pre.manifest, app, spec.kind, ref); }
  catch (err) {
    return { ok: false, kind: spec.kind, stage: 'write', message: `Wrote files but failed to update manifest: ${err instanceof Error ? err.message : String(err)}` };
  }

  if (!pdfRelPath) {
    return {
      ok: false,
      kind: spec.kind,
      stage: 'compile',
      message: 'AI + .typ succeeded but the Typst compile failed. The .typ file is saved; check the log below.',
      log,
    };
  }
  const success: GenerateSuccess = {
    ok: true, kind: spec.kind, texRelPath, texFilename: sourceFilename,
    pdfRelPath, pdfFilename,
  };
  if (log) success.log = log;
  if (spec.postParse) Object.assign(success, spec.postParse(parsed.value));
  return success;
}

// ----- Public entry points -----

export async function generateCv(app: Application): Promise<GenerateOutcome> {
  const pre = await getCommonInputs(app);
  if (!('ok' in pre) || !pre.ok) return pre as GenerateFailure;
  const profileText = pre.profileText;
  return runGeneration<CvDoc>(app, {
    kind: 'cv',
    filenameBase: 'CV',
    systemPrompt: cvSystemPrompt,
    userPrompt: cvUserPrompt({
      profileText,
      jobTitle: app.jobTitle,
      companyName: app.companyName,
      jobUrl: app.jobUrl,
      applyType: typeof app.applyType === 'string' ? app.applyType : 'External Link',
      jobDescription: pre.description,
    }),
    guard: isCvDoc,
    template: cvTemplate,
    maxTokens: 4096,
    postParse: (doc) => ({ hallucinations: checkCvHallucinations(doc, profileText) }),
  }, pre);
}

export async function generateCover(app: Application): Promise<GenerateOutcome> {
  const pre = await getCommonInputs(app);
  if (!('ok' in pre) || !pre.ok) return pre as GenerateFailure;
  const today = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  return runGeneration<CoverDoc>(app, {
    kind: 'cover',
    filenameBase: 'Cover Letter',
    systemPrompt: coverSystemPrompt,
    userPrompt: coverUserPrompt({
      profileText: pre.profileText,
      jobTitle: app.jobTitle,
      companyName: app.companyName,
      jobUrl: app.jobUrl,
      applyType: typeof app.applyType === 'string' ? app.applyType : 'External Link',
      jobDescription: pre.description,
      todayHuman: today,
      previousCovers: pre.previousCovers,
    }),
    guard: isCoverDoc,
    template: coverTemplate,
    maxTokens: 1500,
  }, pre);
}

export async function generateInterview(
  app: Application,
  inputs: InterviewInputs,
): Promise<GenerateOutcome> {
  const pre = await getCommonInputs(app);
  if (!('ok' in pre) || !pre.ok) return pre as GenerateFailure;
  return runGeneration<InterviewDoc>(app, {
    kind: 'interview',
    filenameBase: 'Interview Prep',
    systemPrompt: interviewSystemPrompt,
    userPrompt: interviewUserPrompt({
      profileText: pre.profileText,
      jobTitle: app.jobTitle,
      companyName: app.companyName,
      jobUrl: app.jobUrl,
      jobDescription: pre.description,
      interviewerName: inputs.interviewerName,
      ...(inputs.interviewerRole !== undefined ? { interviewerRole: inputs.interviewerRole } : {}),
      ...(inputs.interviewType !== undefined ? { interviewType: inputs.interviewType } : {}),
    }),
    guard: isInterviewDoc,
    template: interviewTemplate,
    maxTokens: 3000,
  }, pre);
}
